import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, StatCard } from '../components/UI';
import {
  createParentChildProfile,
  enrollChildInClass,
  listParentPortalChildren,
  loadParentChildProgress,
} from '../lib/parentPortalRepository';
import type { ParentChildProgress, ParentPortalChild } from '../lib/parentPortalRepository';
import { useAuth } from '../state/AuthContext';

const levels = ['PG', 'NUR', 'KG', 'C1', 'C2', 'C3', 'C4', 'C5'];

function friendlyLevel(code: string) {
  return ({ PG: 'Playgroup', NUR: 'Nursery', KG: 'KG', C1: 'Class 1', C2: 'Class 2', C3: 'Class 3', C4: 'Class 4', C5: 'Class 5' } as Record<string, string>)[code] ?? code;
}

function ParentPortalWorkspace({ view }: { view: 'dashboard' | 'children' | 'reports' }) {
  const { user } = useAuth();
  const [children, setChildren] = useState<ParentPortalChild[]>([]);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [progress, setProgress] = useState<ParentChildProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [levelCode, setLevelCode] = useState('C1');
  const [language, setLanguage] = useState('en');
  const [saving, setSaving] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  const refreshChildren = useCallback(async (preferredChildId?: string) => {
    const rows = await listParentPortalChildren();
    setChildren(rows);
    setSelectedChildId((current) => preferredChildId || current || rows[0]?.childId || '');
    return rows;
  }, []);

  useEffect(() => {
    setLoading(true);
    refreshChildren()
      .catch((error: Error) => setMessage(error.message))
      .finally(() => setLoading(false));
  }, [refreshChildren]);

  useEffect(() => {
    if (!selectedChildId) { setProgress(null); return; }
    setLoading(true);
    loadParentChildProgress(selectedChildId)
      .then(setProgress)
      .catch((error: Error) => setMessage(error.message))
      .finally(() => setLoading(false));
  }, [selectedChildId]);

  async function submitChild(event: FormEvent) {
    event.preventDefault();
    setMessage('');
    setSaving(true);
    try {
      const childId = await createParentChildProfile(displayName, levelCode, language);
      await refreshChildren(childId);
      setDisplayName('');
      setMessage('Child profile created securely. No child email was required.');
    } catch (error) {
      setMessage((error as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function submitClassJoin(event: FormEvent) {
    event.preventDefault();
    if (!selectedChildId) return;
    setMessage(''); setSaving(true);
    try {
      await enrollChildInClass(joinCode, selectedChildId);
      setJoinCode('');
      setMessage('Class joined securely. The teacher can now assign level-matched learning.');
    } catch (error) { setMessage((error as Error).message); }
    finally { setSaving(false); }
  }

  const selectedChild = children.find((child) => child.childId === selectedChildId) ?? null;
  const title = view === 'children' ? 'Child profiles' : view === 'reports' ? 'Parent reports' : 'Parent dashboard';
  const description = view === 'children'
    ? 'Create and manage protected learner profiles without child email addresses.'
    : view === 'reports'
      ? 'Review secure quiz, Math Tables and Word World summaries.'
      : 'Follow each child’s learning progress from one private family view.';

  return (
    <section className="portal-page parent-portal-page">
      <PageHeader
        title={title}
        description={description}
        actions={<span className="pill green">Family data protected by RLS</span>}
      />
      {message && <p className={`quiz-message ${message.includes('securely') ? 'answer-feedback correct' : 'answer-feedback incorrect'}`}>{message}</p>}

      {children.length > 0 && (
        <div className="panel parent-child-selector">
          <label>
            Viewing learner
            <select value={selectedChildId} onChange={(event) => setSelectedChildId(event.target.value)}>
              {children.map((child) => <option key={child.childId} value={child.childId}>{child.displayName} · {friendlyLevel(child.levelCode)}</option>)}
            </select>
          </label>
          <span>{selectedChild?.relationshipLabel}{selectedChild?.isPrimary ? ' · Primary profile' : ''}</span>
        </div>
      )}

      {loading && <p className="quiz-busy">Loading the protected family summary…</p>}

      {!loading && children.length === 0 && view !== 'children' && (
        <div className="empty-state">
          <img src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
          <h3>No linked child profile yet</h3>
          <p>Create a profile first. QuizPiko never requires a child email address.</p>
          {user?.role === 'parent' && <Link className="button" to="/parent/children">Create child profile</Link>}
        </div>
      )}

      {progress && view !== 'children' && (
        <>
          <div className="stats-grid parent-summary-stats">
            <StatCard label="Weekly quizzes" value={String(progress.weeklyQuizzes)} note={`${progress.quizAccuracyPercent}% quiz accuracy`} />
            <StatCard label="Assignments due" value={String(progress.assignmentsDue)} note="Scheduled or open" tone="amber" />
            <StatCard label="Secure math facts" value={String(progress.secureFacts)} note={`${progress.tableAccuracyPercent}% table accuracy`} tone="green" />
            <StatCard label="Words mastered" value={String(progress.masteredWords)} note={`${progress.wordAccuracyPercent}% word accuracy`} tone="purple" />
          </div>
          <div className="parent-report-grid">
            <article className="panel parent-progress-card">
              <p className="eyebrow">Quiz learning</p>
              <h2>{progress.quizAttempts} completed attempts</h2>
              <progress max="100" value={progress.quizAccuracyPercent} />
              <span>{progress.quizAccuracyPercent}% overall accuracy</span>
              <Link to="/parent/practice">Open secure quiz practice</Link>
            </article>
            <article className="panel parent-progress-card">
              <p className="eyebrow">Math Tables</p>
              <h2>{progress.masteredFacts} mastered facts</h2>
              <progress max="100" value={progress.tableAccuracyPercent} />
              <span>{progress.tableSessions} practice sessions</span>
              <Link to="/learn/tables">Practise Math Tables</Link>
            </article>
            <article className="panel parent-progress-card">
              <p className="eyebrow">English Word World</p>
              <h2>{progress.learningWords} learning words</h2>
              <progress max="100" value={progress.wordAccuracyPercent} />
              <span>{progress.wordSessions} practice sessions</span>
              <Link to="/learn/words">Practise Word World</Link>
            </article>
          </div>
          <p className="parent-last-activity">Last activity: {progress.lastActivityAt ? new Date(progress.lastActivityAt).toLocaleString() : 'No completed activity yet'}</p>
        </>
      )}

      {view === 'children' && (
        <>
        <div className="parent-children-grid">
          {user?.role === 'parent' ? (
            <form className="panel parent-child-form" onSubmit={submitChild}>
              <p className="eyebrow">No child email required</p>
              <h2>Create a child profile</h2>
              <label>Child name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={80} required /></label>
              <label>Level<select value={levelCode} onChange={(event) => setLevelCode(event.target.value)}>{levels.map((level) => <option key={level} value={level}>{friendlyLevel(level)}</option>)}</select></label>
              <label>Learning language<select value={language} onChange={(event) => setLanguage(event.target.value)}><option value="en">English</option><option value="ur">Urdu</option></select></label>
              <button className="button" disabled={saving}>{saving ? 'Creating securely…' : 'Create protected profile'}</button>
              <small>Maximum eight active child profiles per parent account.</small>
            </form>
          ) : (
            <article className="panel parent-child-form">
              <p className="eyebrow">Staging oversight</p>
              <h2>Super Admin view</h2>
              <p>Profile creation is restricted to real parent accounts. This screen provides read-only staging oversight.</p>
            </article>
          )}
          <div className="parent-child-list">
            {children.map((child) => (
              <button key={child.childId} className={child.childId === selectedChildId ? 'selected' : ''} onClick={() => setSelectedChildId(child.childId)}>
                <img src="/brand/piko-parrot-mascot.png" alt="" />
                <span><strong>{child.displayName}</strong><small>{friendlyLevel(child.levelCode)} · {child.preferredLanguage.toUpperCase()}</small></span>
                {child.isPrimary && <em>Primary</em>}
              </button>
            ))}
          </div>
        </div>
        {selectedChildId && (
          <form className="panel parent-class-join-form" onSubmit={submitClassJoin}>
            <div><p className="eyebrow">Teacher class enrolment</p><h2>Join a class safely</h2><p>Enter the join code supplied by the child’s teacher. The class level must match this learner.</p></div>
            <label>Class join code<input value={joinCode} onChange={(event) => setJoinCode(event.target.value.toUpperCase())} placeholder="QP-ABC123" maxLength={32} required /></label>
            <button className="button" disabled={saving}>{saving ? 'Joining securely…' : 'Join class'}</button>
          </form>
        )}
        </>
      )}
    </section>
  );
}

export function ParentDashboard() { return <ParentPortalWorkspace view="dashboard" />; }
export function ParentChildrenPage() { return <ParentPortalWorkspace view="children" />; }
export function ParentReportsPage() { return <ParentPortalWorkspace view="reports" />; }
